import React, { useEffect } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  FormInstance,
  InputNumber,
} from 'antd';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { useWatch } from 'antd/es/form/Form';
import CalendarIcon from 'assets/icons/calendar';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  ContractResponseDto,
} from 'libs/pinnacle-sdk';
import {
  DEFAULT_PREMIUM_PAYMENT_WARRANTY,
  getTransactionDetailInitialValues,
} from './form';
import { calculatePaymentWarrantyDate } from 'components/features/binding/contract-detail/transaction-details/form';
import { getContractStatusLabel, getUnderwritingYearsOptions } from 'utils';
import { SortedSelect } from 'components/ui/sorted-select';
import { formatLocaleNumber } from 'utils/format';

interface TransactionDetailsSectionProps {
  form: FormInstance;
  contract: ContractResponseDto;
}

const TransactionDetailsSection: React.FC<TransactionDetailsSectionProps> = ({
  form,
  contract,
}) => {
  const { t } = useTranslation();

  const inceptionDate = useWatch('inceptionDate', form);
  const premiumPaymentWarranty = useWatch('premiumPaymentWarranty', form);

  const adjustmentType = useWatch('adjustmentType', form);

  const canEditTransactionDetails =
    adjustmentType &&
    adjustmentType !==
      ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate;

  // Calculate Payment Warranty Date when Inception Date or Premium Payment Warranty changes
  useEffect(() => {
    if (inceptionDate && premiumPaymentWarranty) {
      const paymentWarrantyDate = calculatePaymentWarrantyDate(
        inceptionDate,
        premiumPaymentWarranty
      );
      form.setFieldValue('paymentWarrantyDate', paymentWarrantyDate);
    }
  }, [inceptionDate, premiumPaymentWarranty]);

  useEffect(() => {
    if (contract && contract.submission) {
      const data = getTransactionDetailInitialValues(
        contract.submission,
        contract
      );
      form.setFieldsValue(data);
    }
  }, [contract]);

  return (
    <FormSection
      title={t('contractAdjustment.transactionDetailsAndClassification')}
      collapsible
    >
      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          label={t('contractAdjustment.effectiveDate')}
          name="transactionDate"
        >
          <DatePicker
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            defaultValue={dayjs()}
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>

        <Form.Item
          label={t('contractAdjustment.adjustmentType')}
          name="adjustmentType"
        >
          <SortedSelect
            size="large"
            showSearch
            placeholder={t('general.selectField', {
              field: t('contractAdjustment.adjustmentType'),
            })}
            suffixIcon={<ArrowDownIcon />}
            options={Object.values(
              ContractAdjustmentPayloadDtoAdjustmentTypeEnum
            ).map((type) => ({
              value: type,
              label: getContractStatusLabel(t)(type),
            }))}
          />
        </Form.Item>
        <Form.Item
          label={t('contractAdjustment.documentNumber')}
          name="documentNumber"
        >
          <Input disabled size="large" />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={contract.oldPinReferenceNumber ? 4 : 3}>
        <Form.Item
          label={t('contractAdjustment.legacyPINReferenceNumber')}
          name="oldPinReferenceNumber"
        >
          <Input disabled size="large" />
        </Form.Item>

        <Form.Item
          label={t('contractAdjustment.pinnacleReferenceNumber')}
          name="pinnacleReferenceNumber"
        >
          <Input disabled size="large" />
        </Form.Item>

        <Form.Item
          label={t('contractAdjustment.brokerReference')}
          name="brokerReference"
        >
          <Input
            disabled={
              adjustmentType !==
              ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement
            }
            size="large"
          />
        </Form.Item>

        <Form.Item label={t('contractAdjustment.insured')} name="insured">
          <Input disabled size="large" />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          label={t('contractAdjustment.inceptionDate')}
          name="inceptionDate"
        >
          <DatePicker
            size="large"
            disabled={
              !canEditTransactionDetails ||
              adjustmentType ===
                ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement
            }
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>

        <Form.Item label={t('contractAdjustment.expiryDate')} name="expiryDate">
          <DatePicker
            size="large"
            style={{ width: '100%' }}
            disabled={
              !canEditTransactionDetails ||
              adjustmentType ===
                ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement
            }
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
            disabledDate={(current) => current && current < inceptionDate}
          />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          label={t('contractAdjustment.premiumPaymentWarranty')}
          name="premiumPaymentWarranty"
        >
          <InputNumber<number>
            size="large"
            className="w-full"
            precision={0}
            min={1}
            formatter={formatLocaleNumber}
            placeholder={t('general.enter', {
              field: t('contractAdjustment.premiumPaymentWarranty'),
            })}
            disabled={!canEditTransactionDetails}
          />
        </Form.Item>

        <Form.Item
          label={t('contractAdjustment.paymentWarrantyDate')}
          name="paymentWarrantyDate"
        >
          <DatePicker
            disabled
            placeholder={t('general.selectField', {
              field: t('contractAdjustment.paymentWarrantyDate'),
            })}
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={1}>
        <Form.Item
          name="underwritingComments"
          label={t('contractAdjustment.underwritingComments')}
        >
          <TextArea
            rows={4}
            disabled={!canEditTransactionDetails}
            placeholder={t('contractAdjustment.addYourReasonHere')}
          ></TextArea>
        </Form.Item>
      </FormLayoutGroup>
    </FormSection>
  );
};

export default TransactionDetailsSection;
